import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ForumCategory } from '../types';
import { apiClient } from '../services/apiClient';
import { LoadingSpinner } from './LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { canModerate } from '../utils/roles';

export function ForumHomeEnhanced() {
  const { user } = useAuth();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiClient.get('/forum?path=categories');
      
      if (response.data.success) {
        const allCategories = response.data.data;
        // Organize categories hierarchically
        const organized = organizeCategories(allCategories);
        setCategories(organized);
        
        // Auto-expand the Systems category
        const systemsCategory = organized.find(cat => cat.name === 'Συστήματα');
        if (systemsCategory) {
          setExpandedCategories(new Set([systemsCategory.id]));
        }
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError('Σφάλμα κατά τη φόρτωση των κατηγοριών');
    } finally {
      setLoading(false);
    }
  };

  const organizeCategories = (categories: ForumCategory[]): ForumCategory[] => {
    const categoryMap = new Map<string, ForumCategory>();
    const rootCategories: ForumCategory[] = [];
    
    // First pass: create map
    categories.forEach(cat => {
      categoryMap.set(cat.id, { ...cat, subcategories: [] });
    });
    
    // Second pass: build hierarchy
    categories.forEach(cat => {
      const category = categoryMap.get(cat.id)!;
      if (cat.parent_category_id) {
        const parent = categoryMap.get(cat.parent_category_id);
        if (parent) {
          parent.subcategories = parent.subcategories || [];
          parent.subcategories.push(category);
        }
      } else {
        rootCategories.push(category);
      }
    });
    
    // Sort categories and subcategories by display_order
    const sortByOrder = (a: ForumCategory, b: ForumCategory) => 
      (a.display_order || 0) - (b.display_order || 0);
    
    rootCategories.sort(sortByOrder);
    rootCategories.forEach(cat => {
      if (cat.subcategories) {
        cat.subcategories.sort(sortByOrder);
      }
    });
    
    return rootCategories;
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const formatLastActivity = (dateString?: string) => {
    if (!dateString) return 'Καμία δραστηριότητα';
    
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 60) {
      return `${minutes} λεπτά πριν`;
    } else if (hours < 24) {
      return `${hours} ώρες πριν`;
    } else if (days < 7) {
      return `${days} μέρες πριν`;
    } else {
      return date.toLocaleDateString('el-GR');
    }
  };

  const canUserPostInCategory = (category: ForumCategory) => {
    if (category.posting_restrictions === 'disabled') {
      return false;
    }
    if (category.posting_restrictions === 'moderators_only') {
      return user?.role === 'moderator' || user?.role === 'admin';
    }
    return true;
  };

  const getTotalPostCount = (category: ForumCategory): number => {
    let count = category.actual_post_count || category.post_count || 0;
    if (category.subcategories) {
      category.subcategories.forEach(sub => {
        count += sub.actual_post_count || sub.post_count || 0;
      });
    }
    return count;
  };

  const renderCategory = (category: ForumCategory, isSubcategory: boolean = false) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);
    const isSystemCategory = category.is_system_category || category.name === 'Συστήματα';
    const canPost = canUserPostInCategory(category);
    const totalPosts = getTotalPostCount(category);

    return (
      <div key={category.id} className={isSubcategory ? 'ml-8' : ''}>
        <div className={`${!isSubcategory ? 'border-b border-gray-200' : ''}`}>
          <div className={`px-6 py-4 hover:bg-gray-50 transition-colors ${isSubcategory ? 'pl-12' : ''}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4 flex-grow">
                {/* Expand/Collapse Button for parent categories */}
                {hasSubcategories && !isSubcategory && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleCategory(category.id);
                    }}
                    className="mt-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg 
                      className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}

                {/* Category Icon */}
                <div 
                  className={`flex-shrink-0 ${isSubcategory ? 'w-10 h-10' : 'w-12 h-12'} rounded-lg flex items-center justify-center text-white ${isSubcategory ? 'text-sm' : 'text-xl'}`}
                  style={{ backgroundColor: category.color || '#3B82F6' }}
                >
                  {category.icon ? (
                    <i className={category.icon}></i>
                  ) : (
                    <svg className={`${isSubcategory ? 'w-5 h-5' : 'w-6 h-6'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
                    </svg>
                  )}
                </div>

                {/* Category Info */}
                <div className="flex-grow min-w-0">
                  {canPost && !isSystemCategory ? (
                    <Link 
                      to={`/forum/category/${category.id}`}
                      className="block"
                    >
                      <h3 className={`${isSubcategory ? 'text-base' : 'text-lg'} font-semibold text-gray-900 hover:text-blue-600`}>
                        {category.name}
                      </h3>
                    </Link>
                  ) : (
                    <h3 className={`${isSubcategory ? 'text-base' : 'text-lg'} font-semibold text-gray-900`}>
                      {category.name}
                    </h3>
                  )}
                  
                  <div className="flex items-center gap-2 mt-1">
                    {category.is_medical_urgent && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Επείγον
                      </span>
                    )}
                    {category.posting_restrictions === 'moderators_only' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Περιορισμένη
                      </span>
                    )}
                    {isSystemCategory && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        Κατηγορία Συστήματος
                      </span>
                    )}
                  </div>
                  
                  {category.description && (
                    <p className={`mt-1 ${isSubcategory ? 'text-sm' : 'text-sm'} text-gray-600 line-clamp-2`}>
                      {category.description}
                    </p>
                  )}
                  
                  {/* Latest Post Info for subcategories */}
                  {isSubcategory && category.last_post_title && (
                    <div className="mt-2 text-xs text-gray-500">
                      <span className="font-medium">Τελευταία:</span>{' '}
                      <span className="truncate max-w-xs inline-block">
                        {category.last_post_title}
                      </span>
                      {' '}{formatLastActivity(category.last_post_created_at)}
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              {!isSystemCategory && (
                <div className="flex-shrink-0 text-right">
                  <div className={`${isSubcategory ? 'text-xl' : 'text-2xl'} font-bold text-gray-900`}>
                    {isSystemCategory ? totalPosts : (category.actual_post_count || category.post_count || 0)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {(isSystemCategory ? totalPosts : (category.actual_post_count || category.post_count || 0)) === 1 ? 'δημοσίευση' : 'δημοσιεύσεις'}
                  </div>
                  {category.last_post_created_at && !isSubcategory && (
                    <div className="text-xs text-gray-400 mt-1">
                      {formatLastActivity(category.last_post_created_at)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Render Subcategories */}
        {hasSubcategories && isExpanded && (
          <div className="bg-gray-50 border-b border-gray-200">
            {category.subcategories!.map(subcat => renderCategory(subcat, true))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-800 mb-4">{error}</p>
        <button
          onClick={fetchCategories}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Δοκιμάστε ξανά
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Φόρουμ Κοινότητας</h1>
          <p className="mt-2 text-gray-600">
            Συζητήστε με συναδέλφους για θέματα παθολογανατομίας
          </p>
        </div>
        
        {user && (
          <Link
            to="/forum/create"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Νέα Δημοσίευση
          </Link>
        )}
      </div>

      {/* Forum Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-blue-900">
                {categories.reduce((total, cat) => total + getTotalPostCount(cat), 0)}
              </p>
              <p className="text-blue-700">Συνολικές Δημοσιεύσεις</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-green-900">
                {categories.reduce((total, cat) => 
                  total + 1 + (cat.subcategories?.length || 0), 0
                )}
              </p>
              <p className="text-green-700">Κατηγορίες</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-purple-900">Ενεργή</p>
              <p className="text-purple-700">Κοινότητα</p>
            </div>
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Κατηγορίες Φόρουμ</h2>
        </div>

        <div>
          {categories.map(category => renderCategory(category))}
        </div>

        {categories.length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.418 8-9 8a9.013 9.013 0 01-5.314-1.673l-3.563.789a1.5 1.5 0 01-1.777-1.777l.789-3.563A8.963 8.963 0 013 12C3 7.582 7.582 3 12 3s9 3.582 9 9z" />
            </svg>
            <p className="mt-4 text-lg">Δεν υπάρχουν κατηγορίες φόρουμ ακόμα</p>
            <p className="text-sm">Οι κατηγορίες θα εμφανιστούν εδώ όταν δημιουργηθούν</p>
          </div>
        )}
      </div>

      {/* Quick Actions for Moderators */}
      {user && canModerate(user.role) && (
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-yellow-900 mb-4">Ενέργειες Συντονιστή</h3>
          <div className="flex space-x-4">
            <Link
              to="/admin/forum"
              className="inline-flex items-center px-3 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Διαχείριση Φόρουμ
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}